import React from "react";
import {
  List,
  RaRecord,
  Datagrid,
  TextField,
  DateField,
  CreateButton,
  TopToolbar,
  Button,
  FunctionField,
  TextInput,
  FilterButton,
  SelectInput,
  BooleanField,
  Confirm,
  useDelete,
  useNotify,
  useRefresh,
} from "react-admin";
import moment from "moment";
import { Drawer, Paper, Typography, Stack } from "@mui/material";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { SFVirtualNumberCreatePage } from "./CreatePage";
import SFVirtualNumberUpdateDialog from "./UpdateDialog";
import PostAddIcon from "@mui/icons-material/PostAdd";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import {
  statusOptions,
  poolOptions,
  mapToOptions,
} from "./__variables/variables";
import UploadDialog from "./UploadDialog";

const VirtualMsidnFilter = [
  <TextInput label="Type in user phone" source="userPhone" alwaysOn />,
  <TextInput label="virtual number" source="virtualNumber" alwaysOn />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={statusOptions}
    alwaysOn
    data-testid="search-status"
    sx={{ maxWidth: "150px" }}
  />,
  <SelectInput
    key="mapTo"
    source="map_to"
    label="Map To"
    choices={mapToOptions}
    alwaysOn
    data-testid="search-map-to"
  />,
  <SelectInput
    key="pool"
    source="is_pool"
    label="Pool"
    choices={poolOptions}
    alwaysOn
    data-testid="search-pool"
    sx={{ maxWidth: "150px" }}
  />,
];

const ConfirmDialogContent = ({ msisdn }: { msisdn: string }) => {
  return (
    <Paper elevation={0}>
      <Typography variant="body1" gutterBottom>
        {`Are you sure you want to delete number ${msisdn}? This action cannot be
        undone.`}
      </Typography>
    </Paper>
  );
};

const ListActions = (props: any) => {
  const { handleClickOpenDialog, openDialog, handleCloseDialog } = props;

  return (
    <TopToolbar>
      <Stack direction="row" spacing={1}>
        <FilterButton filters={VirtualMsidnFilter} />
        <Button
          label="Batch Add"
          onClick={handleClickOpenDialog}
          data-testid="add-virtualMsisdn-batch"
        >
          <PostAddIcon />
        </Button>
        <UploadDialog openDialog={openDialog} handleClose={handleCloseDialog} />
        <CreateButton label="Create New" />
      </Stack>
    </TopToolbar>
  );
};

export const SFVirtualMsisdnList = (props: any) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const location = useLocation();
  const navigate = useNavigate();
  // --
  const [openDialog, setOpenDialog] = React.useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = React.useState(false);
  const [recordId, setRecordId] = React.useState<string>("");
  const [record, setRecord] = React.useState<any>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] =
    React.useState<boolean>(false);

  const [deleteOne, { data, isLoading: isLoadingDelete, error }] = useDelete(
    "smartfrenVirtualMsisdn",
    { id: record?.id }
  );

  const handleOpenDeleteConfirm = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setDeleteConfirmOpen(true);
  };
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
  };

  const handleClickOpenDialog = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleClickOpenUpdateDialog = (e: any) => {
    e.stopPropagation();
    setOpenUpdateDialog(true);
  };
  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
  };

  const handleClose = React.useCallback(() => {
    navigate("/smartfrenVirtualMsisdn");
  }, [navigate]);

  const matchShow = matchPath(
    "/smartfrenVirtualMsisdn/:id/show/*",
    location.pathname
  );
  const matchCreate = matchPath(
    "/smartfrenVirtualMsisdn/create",
    location.pathname
  );

  const handleDelete = () => {
    deleteOne();
  };
  // --
  React.useEffect(() => {
    if (data) {
      handleCloseUpdateDialog();
      handleCloseDeleteConfirm();
      setRecordId("");
      setRecord(null);
      refresh();
      notify("Msisdn Successfully Deleted", { type: "success" });
    }
  }, [data, notify, refresh]);

  React.useEffect(() => {
    handleCloseUpdateDialog();
    handleCloseDeleteConfirm();
    setRecordId("");
    setRecord(null);
    if (error) {
      Swal.fire({
        icon: "error",
        title: `Request failed: error`,
        text: error as string,
        target: "#muiDrawer",
      });
    }
  }, [error]);

  return (
    <>
      <List
        {...props}
        actions={
          <ListActions
            handleClickOpenDialog={handleClickOpenDialog}
            openDialog={openDialog}
            handleCloseDialog={handleCloseDialog}
          />
        }
        filters={VirtualMsidnFilter}
        resource="smartfrenVirtualMsisdn"
        sort={{ field: "created_at", order: "DESC" }}
        title="Internal &rsaquo; SF Virtual MSISDNs"
      >
        <Datagrid rowClick="edit" bulkActionButtons={false}>
          <DateField source="created_at" label="Created" showTime />
          <TextField source="msisdn" />
          <TextField source="map_to" />
          <FunctionField
            render={(record?: RaRecord) => {
              if (!record) {
                return <span>No data</span>;
              }

              const userPhone = record.user?.phone ?? "";
              let user = "";
              if (record.user) {
                if (record.user.name) {
                  user = record.user.name;
                } else if (record.user.email) {
                  user = record.user.email;
                } else {
                  user = record.user.id;
                }
              }

              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <span>{userPhone}</span>
                  {user && (
                    <span
                      style={{
                        fontSize: "0.8rem",
                        color: "#000000",
                        fontWeight: 700,
                      }}
                    >{`(${user})`}</span>
                  )}
                </div>
              );
            }}
            source="user_id_data"
            label="User"
          />
          <TextField source="status" />
          <BooleanField source="is_pool" />
          <BooleanField source="is_auto_renewed" label="Auto renewal flag" />
          <FunctionField
            label="Activation Date"
            render={(record: any) =>
              record.allocation_date
                ? moment.utc(record.allocation_date).format("DD/MM/YYYY HH:mm")
                : ""
            }
            source="allocation_date"
          />
          <FunctionField
            label="Expire Date"
            render={(record: any) =>
              record.expiry_date
                ? moment.utc(record.expiry_date).format("DD/MM/YYYY HH:mm")
                : ""
            }
            source="expiry_date"
          />
          <FunctionField
            label="Free Date"
            render={(record: any) =>
              record.free_date
                ? moment.utc(record.free_date).format("DD/MM/YYYY HH:mm")
                : ""
            }
            source="free_date"
          />
          <DateField source="updated_at" showTime />
          <FunctionField
            render={(record?: RaRecord) => {
              if (!record) {
                return null;
              }
              return (
                <Stack direction="row" spacing={1}>
                  <Button
                    label="Update"
                    disabled={record.user_id ? true : false}
                    onClick={(e: any) => {
                      setRecordId(record.id as string);
                      handleClickOpenUpdateDialog(e);
                    }}
                    data-testid={`update-btn-${record.msisdn}`}
                  >
                    <EditIcon />
                  </Button>
                  <Button
                    label="Delete"
                    sx={{ color: "error.main" }}
                    disabled={record.user_id ? true : false}
                    onClick={(e: any) => {
                      setRecord(record);
                      handleOpenDeleteConfirm(e);
                    }}
                    data-testid={`update-btn-${record.msisdn}`}
                  >
                    <DeleteIcon />
                  </Button>
                </Stack>
              );
            }}
            source="content"
            label="Action"
            sortable={false}
          />
        </Datagrid>
      </List>
      {/* Update Dialog */}
      {openUpdateDialog && (
        <SFVirtualNumberUpdateDialog
          openUpdateDialog={openUpdateDialog}
          recordId={recordId}
          handleCloseUpdateDialog={handleCloseUpdateDialog}
        />
      )}
      {/* Side Drawer for History */}
      <Drawer
        open={
          !!matchCreate || (!!matchShow && matchShow?.params?.id !== "create")
        }
        anchor="right"
        onClose={handleClose}
      >
        {!!matchCreate && <SFVirtualNumberCreatePage />}
      </Drawer>
      <Confirm
        isOpen={deleteConfirmOpen}
        title="Dear User,"
        content={<ConfirmDialogContent msisdn={record?.msisdn} />}
        onClose={handleCloseDeleteConfirm}
        confirm="Proceed"
        onConfirm={handleDelete}
      />
    </>
  );
};
